import { useState } from 'react';
import { Badge, Button, Card, ErrorState, FIELD_CLASS, Spinner, cx } from '@/components/ui';
import { Modal } from '@/components/Modal';
import { useConfirm } from '@/components/ConfirmProvider';
import { formatDateTime } from '@/lib/format';
import { useMe } from '@/features/auth/useAuth';
import type { Menu } from '@/features/auth/types';
import {
  useAdminMenus,
  useCreateMember,
  useMembers,
  useResetPassword,
  useTerminateMemberSessions,
  useUnlockMember,
  useUpdateMember,
  useUpdateMenus,
} from './useAdmin';
import type { MemberRole, MemberStatus, MemberSummary } from './types';

/**
 * 사용자·권한 관리.
 *
 * 계정은 여기서만 만들어진다. 가입 페이지가 없다. (00-architecture.md 1절)
 *
 * 🔴 카드 나열에서 목록 + 모달로 바꿨다 (2026-08-17, 3.7)
 *   그전에는 회원마다 카드를 통째로 펼쳐 놓았다. 회원이 3~4명이라
 *   세로로 길어질 걱정이 없다고 봤는데, 실제로는 그렇지 않았다.
 *
 *   카드 하나에 입력칸 4개 · 체크박스 5개 · 버튼 4개가 들어간다.
 *   셋만 있어도 화면이 서른 개 넘는 조작으로 덮인다.
 *   "누가 있나" 를 보려고 열었는데 그것을 알려면 스크롤을 한참 내려야 했다.
 *
 *   목록은 "누가 있나" 에 답하고, 모달은 "이 사람을 어떻게 할까" 에 답한다.
 *   질문이 둘이면 화면도 둘이어야 한다.
 *
 * 계정 삭제 버튼이 없다. 비활성화로 대신한다.
 * 지우면 권한 행과 나중에 생길 소유자 참조가 끊긴다.
 */
export function MembersPanel() {
  const menusQuery = useAdminMenus();
  const membersQuery = useMembers();

  /*
    열어둔 회원을 id 로 들고 있다. 객체를 통째로 담지 않는다.

    객체를 담으면 저장 직후 목록은 갱신되는데 모달 안은 옛 값을 계속 보여준다.
    id 로 두면 매번 최신 목록에서 다시 찾으므로 저절로 맞는다.
  */
  const [openId, setOpenId] = useState<number | null>(null);

  if (menusQuery.isLoading || membersQuery.isLoading) {
    return <Spinner />;
  }
  if (menusQuery.error || membersQuery.error) {
    const error = menusQuery.error ?? membersQuery.error;
    return <ErrorState message={error?.message ?? '불러오지 못했습니다.'} />;
  }

  const menus = menusQuery.data ?? [];
  const members = membersQuery.data ?? [];
  const opened = members.find((member) => member.id === openId) ?? null;

  return (
    <div className="flex flex-col gap-4">
      <CreateMemberForm menus={menus} />

      <Card title={`사용자 ${members.length}명`}>
        <ul className="-mx-2 flex flex-col">
          {members.map((member) => (
            <MemberRow key={member.id} member={member} onOpen={() => setOpenId(member.id)} />
          ))}
        </ul>
      </Card>

      <MemberModal
        member={opened}
        menus={menus}
        onClose={() => setOpenId(null)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────
// 목록 한 줄
// ─────────────────────────────────────────────

/**
 * 목록 한 줄. 한눈에 알아야 할 것만 담는다.
 *
 * 담은 것과 뺀 것
 *   담음  아이디 · 표시 이름 · 관리자/비활성/잠김 배지 · 마지막 로그인
 *   뺌    이메일 · 메뉴 권한 · 연속 실패 횟수
 *
 * 뺀 것들은 "이 사람을 어떻게 할까" 를 정한 뒤에 필요한 값이다.
 * 목록에서 답해야 하는 질문은 "누가 있나" 와 "지금 이상한 사람이 있나" 둘뿐이다.
 * 잠김 배지가 목록에 있는 이유가 그것이다 — 열어보기 전에 보여야 하는 신호다.
 */
function MemberRow({ member, onOpen }: { member: MemberSummary; onOpen: () => void }) {
  const { me } = useMe();
  const 본인 = me?.id === member.id;

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-center gap-3 rounded-lg px-2 py-3 text-left transition-colors hover:bg-surface-muted"
      >
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-medium text-fg">{member.loginId}</span>
            {member.role === 'ADMIN' && <Badge tone="info">관리자</Badge>}
            {member.status === 'INACTIVE' && <Badge tone="neutral">비활성</Badge>}
            {member.locked && <Badge tone="danger">잠김</Badge>}
            {본인 && <Badge tone="success">나</Badge>}
          </span>

          <span className="mt-0.5 block truncate text-xs text-fg-muted">
            {member.displayName}
            {' · '}
            마지막 로그인 {member.lastLoginAt ? formatDateTime(member.lastLoginAt) : '없음'}
          </span>
        </span>

        {/* 누를 수 있다는 것을 알려주는 표시. 목록 전체가 버튼이라 화살표만으로 충분하다 */}
        <span aria-hidden="true" className="shrink-0 text-fg-subtle">
          ›
        </span>
      </button>
    </li>
  );
}

// ─────────────────────────────────────────────
// 계정 생성
// ─────────────────────────────────────────────

function CreateMemberForm({ menus }: { menus: Menu[] }) {
  const [open, setOpen] = useState(false);
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('USER');
  const [menuCodes, setMenuCodes] = useState<string[]>([]);

  const createMutation = useCreateMember();

  const reset = () => {
    setLoginId('');
    setPassword('');
    setDisplayName('');
    setEmail('');
    setRole('USER');
    setMenuCodes([]);
  };

  if (!open) {
    return (
      <Button variant="secondary" onClick={() => setOpen(true)}>
        계정 추가
      </Button>
    );
  }

  return (
    <Card title="계정 추가">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-fg-muted">
          아이디
          <input
            className={cx(FIELD_CLASS, 'mt-1')}
            value={loginId}
            // 영문 소문자·숫자·밑줄만 서버가 받는다. 대소문자를 섞으면
            // 나중에 로그인할 때 어느 쪽이었는지 헷갈린다.
            onChange={(event) => setLoginId(event.target.value.toLowerCase())}
            autoCapitalize="none"
          />
        </label>

        <label className="text-xs text-fg-muted">
          표시 이름
          <input
            className={cx(FIELD_CLASS, 'mt-1')}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </label>

        <label className="text-xs text-fg-muted">
          비밀번호 (8자 이상)
          <input
            className={cx(FIELD_CLASS, 'mt-1')}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        <label className="text-xs text-fg-muted">
          이메일 (선택)
          <input
            className={cx(FIELD_CLASS, 'mt-1')}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>

        <label className="text-xs text-fg-muted">
          권한
          <select
            className={cx(FIELD_CLASS, 'mt-1')}
            value={role}
            onChange={(event) => setRole(event.target.value as MemberRole)}
          >
            <option value="USER">일반</option>
            <option value="ADMIN">관리자</option>
          </select>
        </label>
      </div>

      <MenuCheckboxes
        menus={menus}
        selected={menuCodes}
        onChange={setMenuCodes}
        disabled={role === 'ADMIN'}
        note={
          role === 'ADMIN'
            ? '관리자는 메뉴 권한 검사를 건너뜁니다. 체크와 무관하게 전부 볼 수 있습니다.'
            : undefined
        }
      />

      {createMutation.error && (
        <p className="mt-3 text-sm text-danger">{createMutation.error.message}</p>
      )}

      <div className="mt-4 flex gap-2">
        <Button
          disabled={createMutation.isPending || !loginId || password.length < 8 || !displayName}
          onClick={() =>
            createMutation.mutate(
              {
                loginId,
                password,
                displayName,
                email: email.trim() === '' ? null : email.trim(),
                role,
                menuCodes,
              },
              {
                onSuccess: () => {
                  reset();
                  setOpen(false);
                },
              },
            )
          }
        >
          만들기
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          취소
        </Button>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────
// 회원 수정 모달
// ─────────────────────────────────────────────

function MemberModal({
  member,
  menus,
  onClose,
}: {
  member: MemberSummary | null;
  menus: Menu[];
  onClose: () => void;
}) {
  return (
    <Modal open={member !== null} onClose={onClose} labelledBy="member-modal-title">
      {/*
        key 를 붙여 회원이 바뀌면 상태를 처음부터 만든다.

        없으면 A 를 열어 이름을 고치다 닫고 B 를 열었을 때
        A 에 입력하던 값이 B 의 칸에 남는다. useState 의 초기값은
        첫 렌더에서만 쓰이기 때문이다.
      */}
      {member && (
        <MemberForm key={member.id} member={member} menus={menus} onClose={onClose} />
      )}
    </Modal>
  );
}

function MemberForm({
  member,
  menus,
  onClose,
}: {
  member: MemberSummary;
  menus: Menu[];
  onClose: () => void;
}) {
  const { me } = useMe();
  const confirm = useConfirm();
  const 본인 = me?.id === member.id;

  const [displayName, setDisplayName] = useState(member.displayName);
  const [email, setEmail] = useState(member.email ?? '');
  const [role, setRole] = useState<MemberRole>(member.role);
  const [status, setStatus] = useState<MemberStatus>(member.status);
  /*
    화면에 열이 있는 코드만 들고 간다.

    V50 시드가 관리자에게 ADMIN 행까지 넣어두었는데, 그 메뉴는 이제 매트릭스의
    열이 아니다(관리자 화면은 메뉴가 아니라 role 로 막는다). 서버는 열에 없는 코드를
    거부하므로, 받은 값을 그대로 되돌려보내면 체크박스를 건드리지도 않은 저장이 실패한다.

    걸러낸 코드는 다음 저장에서 사라진다. 어차피 아무것도 열지 못하던 행이다.
  */
  const [menuCodes, setMenuCodes] = useState<string[]>(() =>
    member.menuCodes.filter((code) => menus.some((menu) => menu.code === code)),
  );
  const [newPassword, setNewPassword] = useState('');

  const updateMutation = useUpdateMember();
  const menusMutation = useUpdateMenus();
  const passwordMutation = useResetPassword();
  const unlockMutation = useUnlockMember();
  const terminateMutation = useTerminateMemberSessions();

  const error =
    updateMutation.error ??
    menusMutation.error ??
    passwordMutation.error ??
    unlockMutation.error ??
    terminateMutation.error;

  /*
    🔴 저장이 상대를 로그아웃시키는 경우에만 확인창을 띄운다

    강등·비활성화는 서버가 그 사람의 세션을 전부 끊는다(AdminMemberService.update).
    세션에 admin 플래그가 박혀 있어 끊지 않으면 강등이 실제로 적용되지 않기 때문이다.

    이름이나 이메일만 고치는 저장에는 띄우지 않는다.
    모든 저장에 확인을 붙이면 사람은 읽지 않고 누르게 되고,
    그러면 정작 위험한 저장에서도 안 읽는다.
  */
  const 강등 = member.role === 'ADMIN' && role !== 'ADMIN';
  const 비활성화 = member.status === 'ACTIVE' && status !== 'ACTIVE';

  async function save() {
    if (강등 || 비활성화) {
      const 무엇 = 강등 && 비활성화 ? '관리자 권한을 내리고 계정을 비활성화' : 강등 ? '관리자 권한을 내리' : '계정을 비활성화하';

      const ok = await confirm({
        title: `${member.loginId} 계정을 바꿀까요?`,
        description: `${무엇}면 지금 로그인해 있는 모든 기기에서 로그아웃됩니다.`,
        confirmText: '변경',
        tone: 'danger',
      });
      if (!ok) {
        return;
      }
    }

    updateMutation.mutate({
      id: member.id,
      request: {
        displayName,
        email: email.trim() === '' ? null : email.trim(),
        role,
        status,
      },
    });
  }

  async function resetPassword() {
    const ok = await confirm({
      title: `${member.loginId} 의 비밀번호를 바꿀까요?`,
      description:
        '지금 로그인해 있는 모든 기기에서 로그아웃되고, 새 비밀번호를 직접 알려줘야 합니다.',
      confirmText: '재설정',
      tone: 'danger',
    });
    if (!ok) {
      return;
    }

    passwordMutation.mutate(
      { id: member.id, password: newPassword },
      { onSuccess: () => setNewPassword('') },
    );
  }

  async function terminate() {
    const ok = await confirm({
      title: `${member.loginId} 를 모든 기기에서 로그아웃할까요?`,
      description: '비밀번호는 그대로입니다. 본인이 다시 로그인하면 됩니다.',
      confirmText: '로그아웃',
      tone: 'danger',
    });
    if (!ok) {
      return;
    }
    terminateMutation.mutate(member.id);
  }

  return (
    <div className="p-4 sm:p-5">
      <div className="mb-3 flex items-start justify-between gap-2">
        <h2 id="member-modal-title" className="flex flex-wrap items-center gap-1.5">
          <span className="text-base font-semibold text-fg">{member.loginId}</span>
          {member.role === 'ADMIN' && <Badge tone="info">관리자</Badge>}
          {member.status === 'INACTIVE' && <Badge tone="neutral">비활성</Badge>}
          {member.locked && <Badge tone="danger">잠김</Badge>}
          {본인 && <Badge tone="success">나</Badge>}
        </h2>

        <Button variant="ghost" onClick={onClose} aria-label="닫기">
          ✕
        </Button>
      </div>

      <p className="mb-4 text-xs text-fg-muted">
        마지막 로그인 {member.lastLoginAt ? formatDateTime(member.lastLoginAt) : '없음'}
        {member.failedLoginCount > 0 && ` · 연속 실패 ${member.failedLoginCount}회`}
        {member.locked && member.lockedUntil && ` · ${formatDateTime(member.lockedUntil)}까지 잠김`}
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-fg-muted">
          표시 이름
          <input
            className={cx(FIELD_CLASS, 'mt-1')}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </label>

        <label className="text-xs text-fg-muted">
          이메일 (선택)
          <input
            className={cx(FIELD_CLASS, 'mt-1')}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>

        {/*
          🔴 자기 계정의 권한·상태는 바꿀 수 없다
            서버가 A008 로 막지만 화면에서도 막아둔다.
            눌러서 오류를 보는 것보다 누를 수 없는 편이 낫다.
            관리자가 한 명뿐인데 자기 권한을 내리면 되돌려줄 사람이 없다.
        */}
        <label className="text-xs text-fg-muted">
          권한
          <select
            className={cx(FIELD_CLASS, 'mt-1')}
            value={role}
            disabled={본인}
            onChange={(event) => setRole(event.target.value as MemberRole)}
          >
            <option value="USER">일반</option>
            <option value="ADMIN">관리자</option>
          </select>
        </label>

        <label className="text-xs text-fg-muted">
          상태
          <select
            className={cx(FIELD_CLASS, 'mt-1')}
            value={status}
            disabled={본인}
            onChange={(event) => setStatus(event.target.value as MemberStatus)}
          >
            <option value="ACTIVE">활성</option>
            <option value="INACTIVE">비활성</option>
          </select>
        </label>
      </div>

      {본인 && (
        <p className="mt-2 text-xs text-fg-subtle">
          자기 계정의 권한과 상태는 바꿀 수 없습니다. 잠기면 되돌릴 사람이 없습니다.
        </p>
      )}

      <div className="mt-3">
        <Button variant="secondary" disabled={updateMutation.isPending} onClick={() => void save()}>
          정보 저장
        </Button>
      </div>

      <MenuCheckboxes
        menus={menus}
        selected={menuCodes}
        onChange={setMenuCodes}
        disabled={member.role === 'ADMIN'}
        note={
          member.role === 'ADMIN'
            ? '관리자는 메뉴 권한 검사를 건너뜁니다. 체크와 무관하게 전부 볼 수 있습니다.'
            : '변경은 즉시 반영됩니다. 상대가 다시 로그인할 필요가 없습니다.'
        }
      />

      <div className="mt-3">
        <Button
          variant="secondary"
          disabled={menusMutation.isPending}
          onClick={() => menusMutation.mutate({ id: member.id, menuCodes })}
        >
          권한 저장
        </Button>
      </div>

      <hr className="my-4 border-line" />

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-1 text-xs text-fg-muted">
            비밀번호 재설정 (8자 이상)
            <input
              className={cx(FIELD_CLASS, 'mt-1')}
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </label>
          <Button
            variant="secondary"
            disabled={passwordMutation.isPending || newPassword.length < 8}
            onClick={() => void resetPassword()}
          >
            재설정
          </Button>
        </div>

        {/* 재설정하면 그 사람의 세션이 전부 끊긴다. 몇 개가 끊겼는지 알려준다. */}
        {passwordMutation.isSuccess && (
          <p className="text-xs text-ok">
            비밀번호를 바꿨습니다. {passwordMutation.data}개 기기에서 로그아웃되었습니다.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {member.locked && (
            <Button
              variant="secondary"
              disabled={unlockMutation.isPending}
              onClick={() => unlockMutation.mutate(member.id)}
            >
              잠금 해제
            </Button>
          )}

          {/*
            기기를 잃어버렸을 때 쓴다. 자동 로그인 쿠키가 30일짜리라
            그냥 두면 한 달 동안 남이 열어볼 수 있다.
          */}
          <Button
            variant="danger"
            disabled={terminateMutation.isPending}
            onClick={() => void terminate()}
          >
            모든 기기에서 로그아웃
          </Button>
        </div>

        {terminateMutation.isSuccess && (
          <p className="text-xs text-fg-muted">{terminateMutation.data}개 세션을 종료했습니다.</p>
        )}
      </div>

      {error && <p className="mt-3 text-sm text-danger">{error.message}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────
// 메뉴 체크박스
// ─────────────────────────────────────────────

function MenuCheckboxes({
  menus,
  selected,
  onChange,
  disabled = false,
  note,
}: {
  menus: Menu[];
  selected: string[];
  onChange: (codes: string[]) => void;
  disabled?: boolean;
  note?: string;
}) {
  const toggle = (code: string) => {
    onChange(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code]);
  };

  return (
    <div className="mt-4">
      <p className="text-xs font-medium text-fg-muted">접근 가능한 화면</p>

      <div className="mt-2 flex flex-wrap gap-3">
        {menus.map((menu) => (
          <label
            key={menu.code}
            className={cx(
              'flex items-center gap-2 text-sm',
              disabled ? 'text-fg-subtle' : 'text-fg',
            )}
          >
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={selected.includes(menu.code)}
              disabled={disabled}
              onChange={() => toggle(menu.code)}
            />
            {menu.name}
          </label>
        ))}
      </div>

      {note && <p className="mt-2 text-xs text-fg-subtle">{note}</p>}
    </div>
  );
}
